/**
 * FacetPills — v3 multi-select filter pills with color dots and counts
 *
 * Used in LeftNav for Status and Severity facets.
 * Each pill: color dot (6px circle) + label + count right-aligned.
 *
 * Spec: docs/superpowers/specs/2026-03-17-layout-information-density-design.md §1.2
 *
 * "All" pill (selected): rgba(255,255,255,0.7) bg, primary text, shadow (light)
 *                         rgba(2,6,23,0.45) bg, secondary text (dark)
 * Unselected pills: rgba(255,255,255,0.15) bg, secondary text (light)
 *                   rgba(255,255,255,0.04) bg, secondary text (dark)
 */

import { useState } from 'react';

export interface FacetItem {
  key: string;
  label: string;
  count: number;
  dotColor: string;
}

interface FacetPillsProps {
  sectionLabel: string;
  items: FacetItem[];
  selected?: string[];
  onChange?: (selected: string[]) => void;
}

export function FacetPills({ sectionLabel, items, selected: selectedProp, onChange }: FacetPillsProps) {
  const [internalSelected, setInternalSelected] = useState<string[]>(['all']);
  const selected = selectedProp ?? internalSelected;

  const isAllSelected = selected.includes('all') || selected.length === 0;

  const handleToggle = (key: string) => {
    let next: string[];
    if (key === 'all') {
      next = ['all'];
    } else {
      const without = selected.filter(k => k !== 'all' && k !== key);
      if (selected.includes(key)) {
        next = without.length === 0 ? ['all'] : without;
      } else {
        next = [...without, key];
      }
    }
    setInternalSelected(next);
    onChange?.(next);
  };

  const totalCount = items.reduce((sum, i) => sum + i.count, 0);

  return (
    <div data-testid={`facet-${sectionLabel.toLowerCase()}`}>
      {/* Section label — 9px uppercase, secondary text, etched */}
      <div
        className="text-whisper-xs uppercase font-semibold tracking-[0.5px] text-etched mb-2
          text-t2"
      >
        {sectionLabel}
      </div>

      <div className="flex flex-col gap-1">
        {/* "All" pill */}
        <button
          type="button"
          data-testid={`facet-pill-all`}
          onClick={() => handleToggle('all')}
          className={[
            'flex items-center gap-2 px-2 py-1 rounded-v3-pill text-whisper-sm text-etched transition-all duration-150',
            isAllSelected
              ? 'bg-[rgba(255,255,255,0.7)] dark:bg-[rgba(2,6,23,0.45)] shadow-sm text-[#3b3a37] dark:text-[#9a9890] font-medium'
              : 'bg-[rgba(255,255,255,0.15)] dark:bg-[rgba(255,255,255,0.04)] text-t2',
          ].join(' ')}
        >
          <span className="flex-1 text-left">All</span>
          <span className="tabular-nums">{totalCount}</span>
        </button>

        {/* Individual facet pills */}
        {items.map((item) => {
          const isSelected = !isAllSelected && selected.includes(item.key);

          return (
            <button
              key={item.key}
              type="button"
              data-testid={`facet-pill-${item.key}`}
              onClick={() => handleToggle(item.key)}
              className={[
                'flex items-center gap-2 px-2 py-1 rounded-v3-pill text-whisper-sm text-etched transition-all duration-150',
                isSelected
                  ? 'bg-[rgba(255,255,255,0.7)] dark:bg-[rgba(2,6,23,0.45)] shadow-sm text-[#3b3a37] dark:text-[#9a9890] font-medium'
                  : 'bg-[rgba(255,255,255,0.15)] dark:bg-[rgba(255,255,255,0.04)] text-t2',
              ].join(' ')}
            >
              {/* Color dot — 6px circle */}
              <span
                className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ backgroundColor: item.dotColor }}
                aria-hidden="true"
              />
              <span className="flex-1 text-left truncate">{item.label}</span>
              <span className="tabular-nums">{item.count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
